// src/components/BuildingViews/BrewingStationView.js
import React, { useState } from 'react';
import styles from './BrewingStationView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const BrewingStationView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.brewingStation?.activeCrafting;

  const brewingRecipes = [
    {
      id: 'brewHealthPotion',
      name: 'Brew Health Potion',
      description: 'Create a potent healing potion using herbs and berries',
      input: { 'herbs': 2, 'berries': 3 },
      output: { 'healingPotion': 1 },
      duration: 120, // 2 minutes
      icon: 'consumables/healingPotion.png'
    },
    {
      id: 'brewEnergyPotion',
      name: 'Brew Energy Potion',
      description: 'Mix herbs with mushrooms for an energizing brew',
      input: { 'herbs': 3, 'wildMushroom': 2 },
      output: { 'energyPotion': 1 },
      duration: 90, // 1.5 minutes
      icon: 'consumables/energyPotion.png'
    },
    {
      id: 'brewManaPotion',
      name: 'Brew Mana Potion',
      description: 'Create a magical mana restoration potion',
      input: { 'herbs': 4, 'gemShards': 1 },
      output: { 'manaPotion': 1 },
      duration: 150, // 2.5 minutes
      icon: 'consumables/manaPotion.png'
    },
    {
      id: 'brewPoisonPotion',
      name: 'Brew Poison Potion',
      description: 'Craft a dangerous poison using toxic ingredients',
      input: { 'poisonGland': 1, 'herbs': 2 },
      output: { 'poisonPotion': 1 },
      duration: 180, // 3 minutes
      icon: 'crafting/poisonPotion.png'
    },
    {
      id: 'brewStrengthPotion',
      name: 'Brew Strength Potion',
      description: 'Create a potion that temporarily boosts physical power',
      input: { 'bearClaw': 1, 'herbs': 3, 'rawMeat': 1 },
      output: { 'strengthPotion': 1 },
      duration: 200, // 3.33 minutes
      icon: 'consumables/strengthPotion.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    // Call the action through the parent component
    onPlayerAction(recipe.id);
    
    // Reset processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'brewingStation' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'brewingStation' });
  };

  const buildingLevel = playerData.buildings?.brewingStation?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 12;

  return (
    <div className={styles.brewingContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.brewingHeader}>
        <img src="/img/buildingItems/brewingStation.png" alt="Brewing Station" className={styles.brewingIcon} />
        <div className={styles.brewingInfo}>
          <h2>Alchemy & Brewing</h2>
          <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
          <p className={styles.description}>
            Mix magical ingredients to create powerful potions and elixirs
          </p>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🧪 Currently Brewing</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {brewingRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft || activeCrafting ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {Math.floor(recipe.duration * (1 - efficiencyBonus / 100))}s</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Brewing Station Busy' : (isProcessing && isSelected ? 'Brewing...' : 'Start Brewing')}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.brewingTips}>
        <h4>🧪 Brewing Tips</h4>
        <ul>
          <li>Higher quality ingredients yield more potent potions</li>
          <li>Some recipes require rare materials from defeated monsters</li>
          <li>Upgraded brewing stations reduce crafting time</li>
          <li>Experiment with different ingredient combinations</li>
        </ul>
      </div>
    </div>
  );
};

export default BrewingStationView; 